import React, { Dispatch, SetStateAction, useEffect, useRef } from 'react'

import { List } from 'react-window'
import {
  ColumnDef,
  RowSelectionState,
  SortingState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import classNames from 'classnames'
import { arrowDownOutline, arrowUpOutline } from 'ionicons/icons'
import { IonIcon } from '@ionic/react'
import { Checkbox, type CheckboxProps } from 'antd'
import { PileNode } from '@src/generated/schema'
import { Styles } from './VirtualizedTable.styles'

type IIndeterminateInputProps = CheckboxProps & {
  indeterminate?: boolean
  name: string
}

const IndeterminateCheckbox = ({ indeterminate, ...rest }: IIndeterminateInputProps) => {
  return <Checkbox indeterminate={indeterminate} {...rest} />
}

type WithId = { id: string | number }

export interface VirtualizedTableProps<Data extends WithId> {
  columns: Array<ColumnDef<PileNode, any>>
  data: Data[]
  onCellClicked: (e: React.MouseEvent, record: Data, index: number) => void
  onSortChange: (sorting: { id: string; desc: boolean }) => void
  setSelectedRows: Dispatch<SetStateAction<Data[]>>
}

export const VirtualizedTable = <Data extends WithId>({
  columns,
  data,
  setSelectedRows,
  onCellClicked,
  onSortChange,
}: VirtualizedTableProps<Data>) => {
  const skipPageResetRef = useRef(true)

  React.useEffect(() => {
    // After the table has updated, always remove the flag
    skipPageResetRef.current = false
  })

  useEffect(() => {
    skipPageResetRef.current = true
  }, [data])

  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({})
  const [sorting, setSorting] = React.useState<SortingState>([])

  const selectionColumn = React.useMemo<ColumnDef<any, any>>(
    () => ({
      id: 'selection',
      header: ({ table }) => (
        <div>
          <IndeterminateCheckbox
            name='selectAll'
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        </div>
      ),
      cell: ({ row }) => (
        <div>
          <IndeterminateCheckbox
            name={row.id}
            checked={row.getIsSelected()}
            indeterminate={row.getIsSomeSelected?.()}
            onChange={row.getToggleSelectedHandler()}
          />
        </div>
      ),
      enableSorting: false,
      size: 50,
    }),
    [],
  )

  const allColumns = React.useMemo(() => [selectionColumn, ...(columns as any)], [selectionColumn, columns])

  const table = useReactTable({
    columns: allColumns,
    data,
    getRowId: (row: Data) => row.id.toString(),
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    manualSorting: true,
    autoResetAll: !skipPageResetRef.current,
    state: {
      rowSelection,
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
  })

  useEffect(() => {
    if (sorting.length > 0) {
      const sortObj = sorting[0]
      onSortChange(sortObj)
    }
  }, [sorting])

  useEffect(() => {
    setSelectedRows(table.getSelectedRowModel().flatRows.map((row) => row.original))
  }, [rowSelection])

  const RenderRow = React.useCallback(
    ({ index, style, table, onCellClicked }) => {
      const row = table.getRowModel().rows[index]

      return (
        <div
          key={row.id}
          style={{
            ...style,
            display: 'flex',
            width: table.getTotalSize(),
          }}
          className='tr'
        >
          {row.getVisibleCells().map((cell) => {
            const w = cell.column.getSize()

            return (
              <div
                key={cell.id}
                className={classNames(['edit', 'selection'].includes(cell.column.id) ? 'icon-td' : 'td', {
                  isSelected: row.getIsSelected(),
                })}
                style={{
                  width: w,
                  flex: `0 0 ${w}px`,
                }}
                onClick={(event) => {
                  cell.column.id !== 'selection' &&
                    cell.column.id !== 'edit' &&
                    onCellClicked(event, cell.row.original, index)
                }}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </div>
            )
          })}
        </div>
      )
    },
    [table, onCellClicked],
  )

  return (
    <Styles>
      <div className='table'>
        <div>
          {table.getHeaderGroups().map((headerGroup, indexHeaderGroup) => (
            <div
              key={indexHeaderGroup}
              className='tr'
              style={{
                display: 'flex',
                width: table.getTotalSize(),
              }}
            >
              {headerGroup.headers.map((header, indexHeader) => {
                if (header.isPlaceholder) return null

                const column = header.column
                const canSort = column.getCanSort()
                const sortState = column.getIsSorted()
                const w = header.getSize()

                return (
                  <div
                    key={indexHeader}
                    className={['edit', 'selection'].includes(column.id) ? 'icon-th' : 'th'}
                    style={{
                      width: w,
                      flex: `0 0 ${w}px`,
                    }}
                    onClick={canSort ? column.getToggleSortingHandler() : undefined}
                  >
                    {flexRender(column.columnDef.header, header.getContext())}
                    {sortState ? (
                      sortState === 'desc' ? (
                        <IonIcon className={'icon'} mode='md' icon={arrowDownOutline} size='md' />
                      ) : (
                        <IonIcon className={'icon'} mode='md' icon={arrowUpOutline} size='md' />
                      )
                    ) : (
                      ''
                    )}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
        <List
          width={table.getTotalSize() + 20}
          rowCount={table.getRowModel().rows.length}
          rowHeight={60}
          rowComponent={RenderRow}
          // @ts-expect-error react-window v2 typing mismatch
          rowProps={{ table, onCellClicked }}
        />
      </div>
    </Styles>
  )
}
